<template lang="pug">
y-dialog(v-if="showDialog" v-model="showDialog" fullscreen persistent scrollable)
  v-card.neutral200
    v-card-text.pt-8.pb-0
      .mb-4
        .d-flex.align-center.mb-4
          v-btn.mr-4(icon small)
            v-icon(color="primary" @click="back") mdi-close
          .text-2xl.font-semibold.neutral700--text {{ $t('files') }}
      v-row
        v-col(cols=8)
          v-card.white.pa-6.border-outline.round-2xl.mb-4(outlined)
            v-row.pb-2.pb-md-0(no-gutters)
              v-col(cols="12" md="8")
                v-tabs(color="basic700" :height="44")
                  v-tab.text-sm.font-semibold.text-none.px-4 {{ $t('all') }}
              v-col(cols="12" md="4" align="end")
                .d-flex.align-center
                  v-spacer
                  v-btn(@click="clickSearch" icon color="neutral500")
                    v-icon(size="20") $yzi-search-sm
                  v-text-field(v-if="showSearchField" v-model="searchText" @blur="blurSearch" @input="queryChanged" solo dense flat placeholder="Type to search..." hide-details clearable autofocus)
                  v-btn.button-sm.ml-4(color="neutral700" dark depressed @click="showUploadDialog = true") {{ $t('upload') }}
            v-divider
            
            template(v-if="totalCount === 0 && !isFetching")
              v-card.round-xl.text-center.pa-8.border-dashed.mt-4.mb-16(outlined)
                v-avatar.mb-8(color="neutral300" size="54" rounded="circle")
                  v-icon(color="neutral500" size="36") $yzi-users-02
                .mb-1.font-semibold.text-md This patient don’t have any uploaded files yet.
                .text-sm.neutral500--text Start adding files to this patient by clicking “Upload”

            template(v-else)
              .data-table
                v-data-table.mt-sm-4.pb-6(:headers="headers" :items="attachments" item-key="id" :items-per-page="pageSize" hide-default-footer :loading="isFetching" @click:row="selectFile" loader-height="2" :header-props="{ sortIcon: '$yzi-triangle' }" item-class="red")
                  template(v-slot:item.name="{ item }")
                    .h9 {{ item.name }}
                  template(v-slot:item.created="{ item }")
                    div {{ item.created | convertServerDate2 }}
                  template(v-slot:item.uploaded_by="{ item }")
                    template(v-if="item.uploaded_by")
                      v-tooltip(top color="neutral700")
                        template(#activator="{ on, attrs }")
                          div(v-bind="attrs" v-on="on")
                            y-chip.cursor-help(v-bind="attrs" v-on="on" :label="getName(item.uploaded_by)" size="sm" chip-class="pa-1" outlined)
                              template(#prepend)
                                v-avatar.mr-1(size="20" color="neutral300")
                                  v-icon(color="neutral500" size="15") $yzi-user-03
                        div {{ getName(item.uploaded_by) }}
                  template(v-slot:item.action="{ item }")
                    .py-3.text-right
                      v-btn.ml-4(icon small @click="editFile(item)")
                        v-icon(size=20) $yzi-edit-03
                      v-btn.ml-4(icon small @click="downloadFile(item)")
                        v-icon(size=20) $yzi-download-01
                      v-menu(offset-y left rounded="lg" min-width=120)
                        template(#activator="{ on }")
                          v-btn.ml-4(icon small v-on="on")
                            v-icon(size=20) mdi-dots-horizontal
                        v-list.py-0
                          v-divider.basic300
                          v-list-item(@click="deleteItem(item)")
                            .text-sm.danger500--text {{ $t('delete') }}

              .d-flex.align-center.mt-16
                v-spacer
                yc-pagination-custom(v-model="currentPage" :parentPageSize.sync="pageSize" :count="totalCount" text="Item list" @input="pageChanged")

        v-col(cols=4)
          v-card.white.pa-6.border-outline.round-2xl.mb-4(outlined)
            .my-16.py-16.text-center(v-if="!selectedFile")
              .text-md.font-medium.mb-8 {{ $t('no_document_selected') }}
              .neutral500--text {{ $t('no_document_selected_description') }}
            div(v-else)
              .text-md.font-medium.text-center.mb-2 {{ selectedFile.name }}
              .font-medium.mb-3 {{  selectedFile.document.filename }}
              .text-sm.font-medium {{ $t('notes') }}
              .text-md {{ selectedFile.notes && '-' }}
              .text-right.mt-2
                y-button.mr-2(label="Delete" @click="deleteItem(selectedFile)" color="danger" size="sm" underline prependIcon="$yzi-trash-03" iconSize="20")
                y-button(label="View file" @click="viewFile" size="sm" outlined prependIcon="$yzi-eye" iconSize="20")
              v-divider.my-4
              .font-medium.mb-3.text-md {{ $t('file_details') }}
              .mb-4
                .text-sm.font-medium {{ $t('file_type') }}
                .text-md {{ selectedFile.document.type }}
              .mb-4
                .text-sm.font-medium {{ $t('size') }}
                .text-md {{ selectedFile.file_size | bytesToSize }}
              .mb-4
                .text-sm.font-medium {{ $t('created') }}
                .text-md {{ selectedFile.created | convertServerDate2 }}
              div
                .text-sm.font-medium.mb-1 {{ $t('uploaded_by') }}
                y-chip.cursor-help(:label="getName(selectedFile.uploaded_by)" size="sm" chip-class="pa-1" outlined)
                  template(#prepend)
                    v-avatar.mr-1(size="20" color="neutral300")
                      v-icon(color="neutral500" size="15") $yzi-user-03

    v-card-actions.white.pa-0.ma-0
      .d-flex.flex-column.grow
        v-divider
        .d-flex.justify-end.align-center.my-4.mx-8
          v-btn.button-sm.underline.mr-2(text @click="back") {{ $t('cancel') }}
          v-btn.button-sm(depressed color="primary" @click="back") {{ $t('done') }}

    .dialogs
      edit-attachment-dialog(v-if="showEditDialog" :open.sync="showEditDialog" :pid="pid" :file="selectedFile" @onUpdated="updateFile")
      upload-attachment-dialog(v-if="showUploadDialog" :open.sync="showUploadDialog" :pid="pid" @onUploaded="reload")
      delete-attachment-dialog(v-if="showDeleteDialog" :open.sync="showDeleteDialog" :pid="pid" :file="selectedFile" @deleted="reload")
      view-image-dialog(v-if="showViewImageDialog" :open.sync="showViewImageDialog" :image="selectedImage" @closed="selectedImage = null")

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import DeleteAttachmentDialog from './DeleteAttachmentDialog.vue'
import EditAttachmentDialog from './EditAttachmentDialog.vue'
import UploadAttachmentDialog from './UploadAttachmentDialog.vue'
import ViewImageDialog from '@/components/dialogs/ViewImageDialog.vue'

export default {
  name: 'AddEditFileDialog',
  components: { UploadAttachmentDialog, EditAttachmentDialog, DeleteAttachmentDialog, ViewImageDialog },
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    pid: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      searchText: null,
      showSearchField: false,
      pageSize: 20,
      currentPage: 1,
      totalCount: 0,
      isFetching: false,
      headers: [
        { text: 'NAME', value: 'name' },
        { text: 'UPLOAD DATE', value: 'created' },
        { text: 'UPLOADED BY', value: 'uploaded_by' },
        { text: '', value: 'action', sortable: false }
      ],
      attachments: [],
      selectedFile: null,
      showDeleteDialog: false,
      showUploadDialog: false,
      showEditDialog: false,
      showViewImageDialog: false,
      selectedImage: null,
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
    }),
    showDialog: {
      get () {
        return this.open
      },
      set (val) {
        this.$emit('update:open', val)
      }
    },
  },
  mounted () {
    this.fetchAttachments()
  },
  methods: {
    ...mapActions(['showMessage']),
    back () {
      if (!this.isFetching) {
        this.$emit('onUpdateCount', this.attachments)
      }
      this.showDialog = false
    },
    getName (item) {
      return `${item.name}`
    },
    fetchAttachments () {
      this.isFetching = true
      const params = {
        page: this.currentPage,
        page_size: this.pageSize
      }
      this.$axios.get(`/clinics/${this.company.slug}/consultations/${this.pid}/attachments`, { params })
        .then(resp => {
          this.selectedFile = null
          const data = resp.data
          this.attachments = data.results
          this.totalCount = data.count
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    reload () {
      this.fetchAttachments()
    },
    deleteItem (item) {
      this.showDeleteDialog = true
      this.selectedFile = item
    },
    selectFile (item) {
      this.selectedFile = item
    },
    clickSearch () {
    },
    pageChanged () {
      this.fetchAttachments()
    },
    editFile (item) {
      this.selectedFile = item
      this.showEditDialog = true
    },
    downloadFile (item) {
      window.open(item.document.file, '_blank')
    },
    updateFile (item) {
      const findIndex = this.attachments.findIndex(i => i.id === item.id)
      if (findIndex >= 0) {
        this.attachments.splice(findIndex, 1, item)
      }
    },
    viewFile () {
      if (this.getFileType(this.selectedFile.document.type) === 'image') {
        this.viewImage(this.selectedFile.document.file)
      } else if (this.getFileFormat(this.selectedFile.document.type) === 'pdf') {
        this.viewPDF(this.selectedFile.document.file)
      }
    },
    getFileType (type) {
      if (type) {
        const fileType = type.split('/').length > 0 ? type.split('/')[0].toLowerCase() : ''
        return fileType
      }
      return ''
    },
    getFileFormat (format) {
      if (format) {
        const fileFormat = format.split('/').length > 0 ? format.split('/')[1].toLowerCase() : ''
        return fileFormat
      }
      return ''
    },
    viewImage (image) {
      this.selectedImage = image
      this.showViewImageDialog = true
    },
    viewPDF (file) {
      const url = `https://docs.google.com/viewer?url=${file}`
      window.open(url,'_blank')
    }
  },
}
</script>

<style lang="scss" scoped>
.border-outline {
  border: 1px solid #EEEEEE !important;
}
::v-deep .v-data-table__wrapper table tbody tr:hover{
  cursor: pointer;
  background: var(--v-neutral200) !important; 
}
</style>
